import React, { useEffect, useMemo, useState } from 'react';
import CustomInput from 'components/CustomInput/CustomInput';
import { translate } from 'components/Translation';

const SIM_NUMBER_LENGTH = 19;

const prepareData = (data = '') => String(data).replace(/\D+/g, '');
const trimData = (data = '') => prepareData(data).substr(0, SIM_NUMBER_LENGTH);

const number_regex = new RegExp(`^[0-9]{${SIM_NUMBER_LENGTH}}$`);

interface ISIMInputProps {
	inputProps: any;
	helperText?: React.ReactElement | string | undefined;
	onErrorChange: (e: boolean) => void;
	showError: boolean;
	supportAsyncValidation?: boolean;
	[x: string]: any;
}

const CustomSIMInput = ({
	onErrorChange,
	inputProps = {},
	showError,
	helperText,
	supportAsyncValidation,
	...otherProps
}: ISIMInputProps) => {
	const [focused, setFocused] = useState(false);
	const [tooManyDigits, setTooManyDigits] = useState(false);

	useEffect(
		() => () => {
			setFocused(false);
			setTooManyDigits(false);
		},
		[]
	);

	const value = trimData(inputProps.value);
	const isNumberValid = useMemo(() => number_regex.test(value), [value]);

	useEffect(() => {
		onErrorChange && onErrorChange(isNumberValid);
	}, [isNumberValid, onErrorChange]);

	useEffect(() => {
		setTooManyDigits((tooManyDigits) => {
			if (value.length < SIM_NUMBER_LENGTH && tooManyDigits) {
				return false;
			}
			return tooManyDigits;
		});
	}, [value]);

	const onFocus = (...data: any) => {
		setFocused(true);
		inputProps.onFocus && inputProps.onFocus(...data);
	};

	const onChange = (event: any) => {
		const newValue = prepareData(event.target.value);

		newValue.length > SIM_NUMBER_LENGTH && setTooManyDigits(true);
		inputProps.onChange && inputProps.onChange(trimData(newValue));
	};

	let text: React.ReactElement | string | undefined = helperText;

	if (!helperText) {
		if (isNumberValid && !tooManyDigits) {
			text = translate('input.sim.great', SIM_NUMBER_LENGTH);
		} else if ((!isNumberValid && showError) || (isNumberValid && tooManyDigits)) {
			text = translate('input.sim.numberShouldConsistOf', SIM_NUMBER_LENGTH);
		} else if (!isNumberValid && focused) {
			text = translate('input.sim.numberConsistsOf', SIM_NUMBER_LENGTH);
		}
	}

	return (
		<CustomInput
			{...otherProps}
			id="serial-number-input"
			success={!tooManyDigits && isNumberValid && !showError}
			helperText={text}
			error={(showError && !isNumberValid) || tooManyDigits || (supportAsyncValidation && showError)}
			inputProps={{ ...inputProps, onFocus, onChange, value }}
			disableHover
		/>
	);
};

export default CustomSIMInput;
